import { useEffect, useState } from 'react';
import WebApp from '@twa-dev/sdk';
import { fetchSkill, type SkillResponse } from '../lib/api.js';

interface Props {
  skillName: string;       // briefing / meal / recovery / countdown / videos
  title: string;
  icon: string;
  onBack: () => void;
}

export function SkillView({ skillName, title, icon, onBack }: Props) {
  const [data, setData] = useState<SkillResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    fetchSkill(skillName)
      .then((r) => {
        if (!r) {
          setError('서버에 연결할 수 없어요.');
        } else if (!r.ok) {
          if (r.error === 'onboarding_required') {
            setError('먼저 봇에서 온보딩 5단계를 완료해주세요.');
          } else if (r.error === 'not whitelisted') {
            setError('코칭 멤버십이 확인되지 않아요. 코치에게 문의 부탁드려요.');
          } else {
            setError(r.error ?? '데이터를 불러오지 못했어요.');
          }
        } else {
          setData(r);
        }
      })
      .finally(() => setLoading(false));
  }, [skillName]);

  return (
    <div className="app">
      <button className="back-link" onClick={onBack}>← 홈</button>
      <h1>{icon} {title}</h1>

      {loading && (
        <div style={{ marginTop: 24, color: 'var(--tg-hint)' }}>불러오는 중…</div>
      )}

      {error && (
        <div style={{ marginTop: 24, padding: 16, background: 'var(--tg-secondary)', borderRadius: 12 }}>
          <p style={{ color: 'var(--tg-hint)' }}>⚠️ {error}</p>
        </div>
      )}

      {data?.ok && data.text && (
        <div
          style={{
            marginTop: 16,
            padding: 16,
            background: 'var(--tg-secondary)',
            borderRadius: 12,
            whiteSpace: 'pre-wrap',
            fontSize: 14,
            lineHeight: 1.6,
          }}
        >
          {data.text}
        </div>
      )}

      {data?.videos && data.videos.length > 0 && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {data.videos.map((v, i) => (
            <button
              key={i}
              onClick={() => {
                WebApp.HapticFeedback?.selectionChanged?.();
                WebApp.openLink(v.url);
              }}
              style={{
                background: 'var(--tg-secondary)',
                border: 'none',
                borderRadius: 12,
                padding: 0,
                overflow: 'hidden',
                cursor: 'pointer',
                color: 'inherit',
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {v.thumbnailUrl ? (
                <div style={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
                  <img
                    src={v.thumbnailUrl}
                    alt={v.title}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                    loading="lazy"
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      background: 'rgba(0,0,0,0.7)',
                      borderRadius: '50%',
                      width: 56,
                      height: 56,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24,
                    }}
                  >
                    ▶
                  </div>
                  {v.duration && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        background: 'rgba(0,0,0,0.8)',
                        color: '#fff',
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontSize: 12,
                      }}
                    >
                      {v.duration}
                    </div>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    padding: 24,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, #f00 0%, #c00 100%)',
                    color: '#fff',
                    fontSize: 24,
                  }}
                >
                  ▶ YouTube
                </div>
              )}
              <div style={{ padding: 12 }}>
                <strong style={{ fontSize: 14, display: 'block' }}>{v.title}</strong>
                <small style={{ color: 'var(--tg-hint)' }}>{v.url.replace(/^https?:\/\//, '').slice(0, 40)}…</small>
              </div>
            </button>
          ))}
        </div>
      )}

      {data?.buttons && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.buttons.flat().map((btn, i) => (
            <button
              key={i}
              className="btn"
              onClick={() => {
                WebApp.HapticFeedback?.selectionChanged?.();
                if (btn.web_app?.url) {
                  WebApp.openLink(btn.web_app.url);
                } else if (btn.callback_data) {
                  WebApp.showAlert(`Action: ${btn.callback_data}\n(봇에서 더 깊은 응답 가능)`);
                }
              }}
            >
              {btn.text}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
